import axiosInstance from '../utils/axios.instance';
import { API_ENDPOINTS } from '../config/api.config';

/**
 * Service de gestion des commissions
 */
export const commissionService = {
  /**
   * Récupérer toutes les commissions
   * @param {Object} params - Paramètres de pagination
   * @returns {Promise}
   */
  getCommissions: async (params = {}) => {
    try {
      const response = await axiosInstance.get(API_ENDPOINTS.COMMISSIONS.LIST, { params });
      // Le backend retourne { success: true, data: { content: [...] } }
      if (response.data?.content) {
        return response.data.content;
      }
      return response.data || response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Récupérer les commissions actives uniquement
   * @returns {Promise}
   */
  getActiveCommissions: async () => {
    try {
      const response = await axiosInstance.get(API_ENDPOINTS.COMMISSIONS.ACTIVE);
      // Le backend retourne { success: true, data: { content: [...] } } ou un tableau direct
      if (response.data?.content) {
        return response.data.content;
      }
      return response.data || response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Récupérer une commission par son ID
   * @param {number} commissionId
   * @returns {Promise}
   */
  getCommissionById: async (commissionId) => {
    try {
      const response = await axiosInstance.get(API_ENDPOINTS.COMMISSIONS.BY_ID(commissionId));
      return response.data || response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Créer une nouvelle commission (ADMIN)
   * @param {Object} commissionData
   * @returns {Promise}
   */
  createCommission: async (commissionData) => {
    try {
      const response = await axiosInstance.post(API_ENDPOINTS.COMMISSIONS.CREATE, commissionData);
      return response.data || response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Modifier une commission (ADMIN/ADMIN)
   * @param {number} commissionId
   * @param {Object} commissionData
   * @returns {Promise}
   */
  updateCommission: async (commissionId, commissionData) => {
    try {
      const response = await axiosInstance.put(
        API_ENDPOINTS.COMMISSIONS.UPDATE(commissionId),
        commissionData
      );
      return response.data || response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Supprimer une commission (ADMIN/ADMIN)
   * @param {number} commissionId
   * @returns {Promise}
   */
  deleteCommission: async (commissionId) => {
    try {
      const response = await axiosInstance.delete(API_ENDPOINTS.COMMISSIONS.DELETE(commissionId));
      return response.data || response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Rechercher des commissions
   * @param {string} query
   * @returns {Promise}
   */
  searchCommissions: async (query) => {
    try {
      const response = await axiosInstance.get(API_ENDPOINTS.COMMISSIONS.SEARCH, {
        params: { q: query },
      });
      // Le backend retourne { success: true, data: { content: [...] } }
      if (response.data?.content) {
        return response.data.content;
      }
      return response.data || response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Récupérer les membres d'une commission
   * @param {number} commissionId
   * @returns {Promise}
   */
  getCommissionMembers: async (commissionId) => {
    try {
      const response = await axiosInstance.get(
        API_ENDPOINTS.COMMISSIONS.MEMBERS(commissionId)
      );
      return response.data || response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Ajouter un membre à une commission (ADMIN/ADMIN)
   * @param {number} commissionId
   * @param {Object} memberData - { userId, role }
   * @returns {Promise}
   */
  addCommissionMember: async (commissionId, memberData) => {
    try {
      const response = await axiosInstance.post(
        API_ENDPOINTS.COMMISSIONS.ADD_MEMBER(commissionId),
        memberData
      );
      return response.data || response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Retirer un membre d'une commission (ADMIN/ADMIN)
   * @param {number} commissionId
   * @param {number} memberId
   * @returns {Promise}
   */
  removeCommissionMember: async (commissionId, memberId) => {
    try {
      const response = await axiosInstance.delete(
        API_ENDPOINTS.COMMISSIONS.REMOVE_MEMBER(commissionId, memberId)
      );
      return response.data || response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Changer le rôle d'un membre (ADMIN/ADMIN)
   * @param {number} commissionId
   * @param {number} memberId
   * @param {string} role - PRESIDENT, VICE_PRESIDENT, SECRETARY, TREASURER, MEMBER, OBSERVER
   * @returns {Promise}
   */
  updateMemberRole: async (commissionId, memberId, role) => {
    try {
      const response = await axiosInstance.put(
        API_ENDPOINTS.COMMISSIONS.UPDATE_MEMBER_ROLE(commissionId, memberId),
        { role }
      );
      return response.data || response;
    } catch (error) {
      throw error;
    }
  },
};

export default commissionService;
